import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Empty from '../../../../assets/emptyUnapproved.png';
import { NewOrderTypes } from '../../../../common/enums/enums';
import EditServicePopup from '../../../../components/EditServicePopup';
import ServiceRow from '../../../../components/ServiceRow';
import {
  setServiceOrderSelectedServicesPrices,
  setServiceOrderServicesComplianceTime,
} from '../../../../redux/actions/orders/newServiceOrderActions';
import { selectUserDepartments } from '../../../../redux/selectors/userDataSelectors';
import EmptyState from '../../components/EmptyState';
import { getSelectorActionsBasedOnType } from '../helper';
import ListServices from './components/ListServices';

export default function Services({
  onValidStep,
  boxProps = { p: { xs: 2, md: 6 } },
  overflow,
  orderType = NewOrderTypes.SIMPLE_ORDER,
  setSelectedVendorDepartment,
}) {
  const { t } = useTranslation('orders');
  const dispatch = useDispatch();
  const {
    getServicesByDepartmentAction,
    addPriceToSelectedPricesAction,
    removePriceToSelectedPricesAction,
    alternativeOwnerAction,
    setAlternativeOwnerAction,
    selectIsLoadingServicesSelector,
    selectPricesGroupedSelector,
    selectedDepartmentSelector,
    selectedPricesSelector,
    selectedVendorSelector,
    selectedCategoriesSelector,
  } = getSelectorActionsBasedOnType(orderType);
  const [selectedService, setSelectedService] = useState(null);
  const departments = useSelector(selectUserDepartments);
  const selectedVendor = useSelector(selectedVendorSelector);
  const selectedDepartment = useSelector(selectedDepartmentSelector);
  const serviceOrderSelectedPrices = useMemo(() => [], []);
  const selectedPrices =
    useSelector(selectedPricesSelector) || serviceOrderSelectedPrices;
  const selectedCategories = useSelector(selectedCategoriesSelector);
  const pricesGrouped = useSelector(selectPricesGroupedSelector);
  const loading = useSelector(selectIsLoadingServicesSelector);

  useEffect(() => {
    if (setSelectedVendorDepartment)
      setSelectedVendorDepartment(selectedDepartment && selectedVendor);
  }, [selectedDepartment, selectedVendor, setSelectedVendorDepartment]);

  useEffect(
    () => onValidStep(!!selectedPrices?.length),
    [onValidStep, selectedPrices]
  );

  const handleChangeDepartment = event => {
    dispatch(
      getServicesByDepartmentAction({
        vendorId: selectedVendor.companyId,
        department: event.target.value,
      })
    );
    dispatch(
      setAlternativeOwnerAction({
        personId: null,
        label: '',
      })
    );
    dispatch(
      alternativeOwnerAction({
        companyId: event.target.value.companyId,
        departmentId: event.target.value.departmentId,
      })
    );
  };

  const actionBySelected = {
    true: addPriceToSelectedPricesAction,
    false: removePriceToSelectedPricesAction,
  };

  const toggleSelectPrice = (price, selected) => {
    dispatch(actionBySelected[selected](price));
  };

  const listOverflow = (overflow && { maxHeight: 300, overflow: 'auto' }) || {};

  const setSelectedPrices = price => {
    dispatch(setServiceOrderSelectedServicesPrices(price));
  };

  const onSelectPrice = (price, isCheck) => {
    if (!isCheck) {
      setSelectedService(price);
    }

    if (isCheck) {
      setSelectedPrices(
        selectedPrices.filter(s => s.servicePriceId !== price.servicePriceId)
      );
    }
  };
  const setServiceOrderComplianceTime = () => {
    if (
      orderType === NewOrderTypes.SERVICE_ORDER &&
      selectedPrices.length > 0
    ) {
      const complianceTime = selectedPrices.reduce(
        (acc, element) => acc + element?.localComplianceTime,
        0
      );
      dispatch(setServiceOrderServicesComplianceTime(complianceTime));
    }
  };

  const saveChangesOfService = newData => {
    setSelectedService(null);
    setSelectedPrices([
      ...(selectedPrices || []).filter(sp => {
        return sp.servicePriceId !== newData.servicePriceId;
      }),
      newData,
    ]);
  };

  useEffect(() => {
    setServiceOrderComplianceTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrices]);

  return (
    <>
      <EditServicePopup
        service={selectedService}
        onClose={() => setSelectedService(null)}
        onSave={saveChangesOfService}
      />
      <Box {...boxProps} pb={2} mb={{ xs: 25, md: 'unset' }}>
        <Box>
          <Typography variant="h6" mb={4}>
            {t('newOrder.services.title')}
          </Typography>
        </Box>
        <Box width={{ xs: '60%', md: '35%' }} mb={4}>
          <FormControl fullWidth>
            <InputLabel htmlFor="grouped-native-select">
              {t('newOrder.services.department')}
            </InputLabel>
            <Select
              sx={{ bgcolor: 'background.paper' }}
              id="selectDepartments"
              variant="outlined"
              label="Department"
              data-testid="select-department"
              disabled={!selectedVendor}
              value={selectedDepartment}
              onChange={handleChangeDepartment}
            >
              {departments?.map(department => (
                <MenuItem key={department.departmentId} value={department}>
                  {department.departmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {selectedCategories?.length > 1 && (
          <Box mb={4}>
            <Alert
              sx={{
                '& .MuiAlert-icon': {
                  color: theme => theme.palette.alert.color,
                },
                backgroundColor: theme => theme.palette.alert.background,
              }}
              severity={'info'}
            >
              {t('newOrder.multipleOrders.services')}
            </Alert>
          </Box>
        )}
        <EmptyState
          img={Empty}
          width={86}
          pt={4}
          pb={3}
          show={!selectedDepartment}
          subtitle={t('newOrder.services.emptyState')}
        />
        {loading ? (
          <Box sx={{ columns: { xs: 1, md: 2 } }}>
            {[...new Array(6)].map((item, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width={'100%'}
                height={40}
                sx={{ marginBottom: '16px' }}
                data-testid="skeleton"
              />
            ))}
          </Box>
        ) : (
          <>
            {orderType === NewOrderTypes.SERVICE_ORDER && selectedDepartment && (
              <Typography
                mt={{ xs: 1, sm: 2 }}
                mb={{ xs: 1, sm: 2 }}
                sx={{ fontSize: '12px', textTransform: 'uppercase' }}
              >
                {t(`newServiceOrder.infoTitle`)}:
              </Typography>
            )}

            {Object.entries(pricesGrouped).map(([key, prices]) => (
              <Box key={key}>
                {orderType === NewOrderTypes.SIMPLE_ORDER ? (
                  <>
                    <Typography
                      sx={{ fontSize: '12px', textTransform: 'uppercase' }}
                    >
                      {t(`newOrder.services.category.${key}`)}:
                    </Typography>
                    <Stack {...listOverflow}>
                      <ListServices
                        prices={prices}
                        selectedPrices={selectedPrices}
                        onSelectPrice={toggleSelectPrice}
                      />
                    </Stack>
                  </>
                ) : (
                  prices?.map(service => (
                    <ServiceRow
                      key={service.servicePriceId}
                      service={service}
                      selectedPrices={selectedPrices}
                      onSelectPrice={onSelectPrice}
                      onOpenEstimateDialog={serviceInfo => {
                        setSelectedService(serviceInfo);
                      }}
                    />
                  ))
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </>
  );
}
